import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { displays } from 'investira.react.lib';
import { Card, Stack, Typography, Chip } from 'investira.react.components';
import { PessoaImage, MutedCopy } from '../../../components/molecules';
import { ATIVO_MERCADO, ATIVO_TIPO, PUBLICO_ALVO } from '@investirapri/financa.js/lib/enums';
import { BENCHMARKS, FUNDO_SITUACAO, PATHS } from '../../../enums';
import { formats, validators } from 'investira.sdk';

const AtivosItem = memo(props => {
  const {
    logo_pessoa_id,
    logo_ok,
    ativo,
    ifeed_ativo_id,
    ativo_id,
    ativo_seuid,
    mercado,
    emissor,
    emissor_cnpj,
    cnpj,
    isin,
    ticker,
    serie,
    vencimento,
    situacao,
    ativo_tipo_id,
    estruturado,
    qualificado,
    benchmark_indicador_id,
    publico_alvo,
    esg,
    exclusivo,
    pl,
    cotistas,
    aplicacao_min
  } = props.data;

  const xPath = PATHS[mercado.toLowerCase()].path;
  const xAtivoId = ifeed_ativo_id || ativo_id;
  const xPrimaryChipLabel = props.showAtivoTipo
    ? ATIVO_TIPO[ativo_tipo_id]?.descricao
    : ATIVO_MERCADO[mercado]?.descricao;
  const xFundoSituacao = Object.values(FUNDO_SITUACAO)
    .filter(pItem => pItem.value !== FUNDO_SITUACAO.EM_FUNCIONAMENTO_NORMAL.value)
    .find(pItem => pItem.value === situacao);
  const xBenchmark = BENCHMARKS[benchmark_indicador_id];
  const xPublicoAlvo = Object.values(PUBLICO_ALVO).find(
    pItem => String(pItem.id) === String(publico_alvo)
  );

  return (
    <Card component="article">
      <Link
        to={`/ativos${xPath}/${xAtivoId}`}
        aria-label={`Ver detalhes de ${ticker || ativo || ativo_seuid}`}>
        <Stack direction="row" px={2} py={2} spacing={2}>
          <Stack>
            <PessoaImage
              logoPessoaId={logo_pessoa_id}
              alt={`Logo ${emissor?.pessoa_fantasia || ativo}`}
              hasLogo={logo_ok}
              mercado={mercado}
              size={40}
            />
          </Stack>
          <Stack flexGrow={1} minWidth={0} spacing={1}>
            <Stack flexGrow={1} direction="row">
              <Stack flexGrow={1} minWidth={0}>
                <Stack flexGrow={1}>
                  <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600 }}>
                    {ticker || ativo_seuid || ativo}
                  </Typography>
                </Stack>
                {/* TODO: SE fundo mostrar o gestor e adm ao invés de ativo */}
                <Stack direction="row" flexGrow={1} spacing={1} mb={1}>
                  {ativo && (
                    <Typography variant="body2" color="textSecondary">
                      {ativo}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" flexGrow={1} spacing={1}>
                  {emissor?.pessoa_fantasia && (
                    <Typography variant="body2" color="textSecondary">
                      {emissor?.pessoa_fantasia}
                    </Typography>
                  )}
                </Stack>
              </Stack>
              <Stack alignItems="flex-end" ml={1} minWidth={0} maxWidth={{ xs: '45%', sm: 'none' }}>
                <Stack justifyContent="end" maxWidth="100%">
                  {xPrimaryChipLabel && (
                    <Chip
                      label={xPrimaryChipLabel}
                      size="small"
                      color="primary"
                      sx={{
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>
            <Stack sx={{ gap: '8px 8px' }} flexWrap="wrap" alignItems="center" direction="row">
              {(emissor_cnpj || cnpj) && (
                <Chip label={displays.cnpj(emissor_cnpj || cnpj)} size="small" color="secondary" />
              )}
              {isin && (
                <MutedCopy value={isin}>
                  <Chip label={isin} size="small" color="secondary" />
                </MutedCopy>
              )}
              {serie && <Chip label={`Série: ${serie}`} size="small" color="secondary" />}
              {vencimento && (
                <Chip
                  label={`Vencimento: ${formats.formatDate(vencimento)}`}
                  size="small"
                  color="secondary"
                />
              )}
              {estruturado && <Chip label="Estruturado" size="small" color="secondary" />}
              {qualificado && <Chip label="Qualificado" size="small" color="secondary" />}
              {xPublicoAlvo && (
                <Chip label={xPublicoAlvo.descricao} size="small" color="secondary" />
              )}
              {xBenchmark && (
                <Chip label={xBenchmark.indicador_sigla} size="small" color="secondary" />
              )}
              {esg && <Chip label="ESG" size="small" color="secondary" />}
              {exclusivo && <Chip label="Exclusivo" size="small" color="secondary" />}
              {validators.isNumber(aplicacao_min) && aplicacao_min > 0 && (
                <Chip
                  label={`Aplicação mínima: ${formats.friendlyNumber(aplicacao_min, 2, true)}`}
                  size="small"
                  color="secondary"
                />
              )}
              {validators.isNumber(pl) && pl > 0 && (
                <Chip
                  label={`Patrimônio liquido: ${formats.friendlyNumber(pl, 2, true)}`}
                  size="small"
                  color="secondary"
                />
              )}
              {validators.isNumber(cotistas) && cotistas > 0 && (
                <Chip
                  label={`Cotistas: ${formats.friendlyNumber(cotistas, 2)}`}
                  size="small"
                  color="secondary"
                />
              )}
              {xFundoSituacao && (
                <Chip
                  label={xFundoSituacao.descricao}
                  size="small"
                  color={xFundoSituacao.color}
                  variant="outlined"
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Link>
    </Card>
  );
});

export default AtivosItem;
